import { Inject, LOCALE_ID, Pipe, PipeTransform } from "@angular/core";
import { MacroNumbersDetails } from "../models/enum/macroNumber.enum";
import { DecimalPipe } from "@angular/common";
import { getCurrencySymbol } from "../shared/utils/helper-number-format";

@Pipe({
  name: "macroNumber",
})
export class MacroNumberPipe implements PipeTransform {
  constructor(
    private decimalPipe: DecimalPipe,
    @Inject(LOCALE_ID) private localeId: string,
  ) {}

  transform(value: number, symbol?: string): string {
    if (value === null || value === undefined) {
      return "";
    }

    // This pipe is reused across many screens (recent activity, earning history, manage
    // symbols/accounts, table cells) and not all of them show the ISO code alongside the
    // amount - "symbol" (not "narrowSymbol") avoids collapsing AUD/USD/NZD/CAD to an
    // ambiguous bare "$" on the ones that don't.
    const displaySymbol = symbol ? getCurrencySymbol(symbol, this.localeId, "symbol") : "";
    return this.formatAmount(value, displaySymbol);
  }

  formatAmount(amount: number, symbol: string): string {
    const details = MacroNumbersDetails.find((details) => Math.abs(amount) >= details.amount);

    if (details) {
      return this.format(symbol, amount, details);
    }

    const digitsInfo = this.getDigitsInfoForSmallAmount(amount);
    return `${symbol}${this.decimalPipe.transform(amount, digitsInfo)}`;
  }

  private getDigitsInfoForSmallAmount(amount: number): string {
    const absoluteAmount = Math.abs(amount);
    if (Number.isInteger(absoluteAmount)) {
      return "1.0-0";
    }

    const integerDigits = absoluteAmount >= 1 ? Math.floor(Math.log10(absoluteAmount)) + 1 : 1;
    if (integerDigits <= 1) {
      return "1.2-2";
    }
    if (integerDigits === 2) {
      return "1.1-1";
    }
    return "1.0-0";
  }

  format(
    symbol: string,
    amount: number,
    macroNumberDetails: { symbol: string; amount: number },
  ): string {
    const { symbol: baseSymbol, amount: baseAmount } = macroNumberDetails;
    const sign = amount < 0 ? "-" : "";
    const absoluteAmount = Math.abs(amount);
    const divided = absoluteAmount / baseAmount;
    const digits = Math.floor(Math.log10(divided)) + 1;
    const decimals = Math.max(0, 3 - digits);
    const digitsInfo = `1.${decimals}-${decimals}`;
    const formattedAmount = this.decimalPipe.transform(divided, digitsInfo);
    return `${symbol}${sign}${formattedAmount}${baseSymbol}`;
  }
}
